// src/Components/SelectionGrid.js

import React, { useState, useEffect } from 'react';
import { useNavigate} from 'react-router-dom'; // 1. Add useLocation
import { EditorialCard } from '../Utility/EditorialCard';
import { fetchSelectionData, fetchTestTitle } from './SelectionService'; // Import "O" function

const SelectionGrid = () => {
    // --- STATE MANAGEMENT ---
    const navigate = useNavigate();
    const [counter, setCounter] = useState(0); // 0: Grade, 1: Subject, 2: Chapter
    
    // ID Storage
    const [gradeId, setGradeId] = useState(null);
    const [subjectId, setSubjectId] = useState(null);
    const [chapterId, setChapterId] = useState(null); // Reserved for later

    // Data & UI State
    const [allItems, setAllItems] = useState([]);      // Raw data from DB
    const [displayedItems, setDisplayedItems] = useState([]); // Filtered data
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [pageTitle, setPageTitle] = useState('Select Grade');

    // --- "O" FUNCTION CALLER ---
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setSearchTerm(''); // Reset search on transition
            
            // Determine Title based on Counter
            if (counter === 0) setPageTitle("Select Grade");
            else if (counter === 1) setPageTitle("Select Subject");
            else if (counter === 2) setPageTitle("Select Chapter");

            // Call the imported "O" function
            const data = await fetchSelectionData(counter, gradeId, subjectId);
            
            setAllItems(data);
            setDisplayedItems(data);
            setLoading(false);
        };

        loadData();
    }, [counter, gradeId, subjectId]); // Re-run when counter or IDs change

    // --- SEARCH LOGIC (Client Side) ---
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        
        if (!term.trim()) {
            setDisplayedItems(allItems);
            return;
        }

        const filtered = allItems.filter(item => 
            item.name && item.name.toLowerCase().includes(term.toLowerCase())
        );
        setDisplayedItems(filtered);
    };

    // --- CARD CLICK LOGIC (Forward Navigation) ---
    const handleCardClick = async (item) => {
        if (counter === 0) {
            // Selected a Grade -> Go to Subjects
            setGradeId(item.id);
            setCounter(1);
        } else if (counter === 1) {
            // Selected a Subject -> Go to Chapters
            setSubjectId(item.id);
            setCounter(2);
        } else if (counter === 2) {
            // Selected a Chapter -> (Logic for later)
            setChapterId(item.id);
            let title = await fetchTestTitle(item.id);
            console.log("Chapter Selected:", title, item.id);
            navigate('/topics', {
                state: {
                    title: title,
                    chapterId: item.id
                }
            });
        }
    };

    // --- BACK BUTTON LOGIC (Backward Navigation) ---
    const handleBack = () => {
        if (counter === 0) {
            navigate(-1) 
        } else if (counter === 1) {
            // Back from Subjects to Grades
            setGradeId(null);
            setCounter(0);
        } else if (counter === 2) {
            // Back from Chapters to Subjects
            setSubjectId(null);
            setCounter(1);
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 min-h-screen">
            
            {/* --- Header Section --- */}
            <div className="mb-6">
                {/* Hide Back button on Step 0 if preferred, or redirect home */}
                <button 
                    onClick={handleBack} 
                    className={`text-sm mb-2 flex items-center transition-colors text-gray-500 hover:text-gray-800`}
                >
                    <span className="mr-1">&larr;</span> Back
                </button>

                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-tight">
                    {pageTitle}
                </h1>
            </div>

            {/* --- Search Bar --- */}
            <div className="mb-8 relative">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    placeholder={`Search...`}
                    className="w-full p-4 pl-12 rounded-lg bg-gray-100 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg transition-all placeholder-gray-400"
                />
                <svg
                    className="w-6 h-6 absolute left-4 top-4 text-gray-400 pointer-events-none"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>

            {/* --- Loading State --- */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                /* --- Grid Layout --- */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {displayedItems.map((item, index) => (
                        <div key={item.id} className="cursor-pointer">
                            <EditorialCard
                                index={index}
                                title={item.name}
                                onClick={() => handleCardClick(item)}
                            />
                        </div>
                    ))}

                    {displayedItems.length === 0 && (
                        <div className="col-span-full flex flex-col items-center justify-center text-gray-400 py-12">
                            <p className="text-lg">No items found matching "{searchTerm}"</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectionGrid;